/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { PureComponent } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../common'; // @ts-ignore-next-line
// not generating typescript files or definitions for the generated JS components
// because we'd need to dynamically know if we're importing the
// TS file (dev/docs) or the JS file (distributed), and it's more effort than worth
// to generate & git track a TS module definition for each icon component

import { icon as empty } from './assets/empty.js';
import { enqueueStateChange } from '../../services/react';
import { htmlIdGenerator } from '../../services';
var typeToPathMap = {
  accessibility: 'accessibility',
  addDataApp: 'app_add_data',
  advancedSettingsApp: 'app_advanced_settings',
  aggregate: 'aggregate',
  alert: 'alert',
  annotation: 'annotation',
  apmApp: 'app_apm',
  apmTrace: 'apm_trace',
  apps: 'apps',
  arrowDown: 'arrow_down',
  arrowLeft: 'arrow_left',
  arrowRight: 'arrow_right',
  arrowUp: 'arrow_up',
  asterisk: 'asterisk',
  auditbeatApp: 'app_auditbeat',
  beaker: 'beaker',
  bell: 'bell',
  bellSlash: 'bellSlash',
  bolt: 'bolt',
  boxesHorizontal: 'boxes_horizontal',
  boxesVertical: 'boxes_vertical',
  branch: 'branch',
  broom: 'broom',
  brush: 'brush',
  bug: 'bug',
  bullseye: 'bullseye',
  calendar: 'calendar',
  canvasApp: 'app_canvas',
  codeApp: 'app_code',
  check: 'check',
  checkInCircleFilled: 'checkInCircleFilled',
  cheer: 'cheer',
  clock: 'clock',
  cloudDrizzle: 'cloudDrizzle',
  cloudStormy: 'cloudStormy',
  cloudSunny: 'cloudSunny',
  compute: 'compute',
  console: 'console',
  consoleApp: 'app_console',
  controlsHorizontal: 'controls_horizontal',
  controlsVertical: 'controls_vertical',
  copy: 'copy',
  copyClipboard: 'copy_clipboard',
  createAdvancedJob: 'ml_create_advanced_job',
  createMultiMetricJob: 'ml_create_multi_metric_job',
  createPopulationJob: 'ml_create_population_job',
  createSingleMetricJob: 'ml_create_single_metric_job',
  cross: 'cross',
  crossClusterReplicationApp: 'app_cross_cluster_replication',
  crosshairs: 'crosshairs',
  crossInACircleFilled: 'crossInACircleFilled',
  currency: 'currency',
  cut: 'cut',
  dashboardApp: 'app_dashboard',
  database: 'database',
  dataVisualizer: 'ml_data_visualizer',
  devToolsApp: 'app_devtools',
  discoverApp: 'app_discover',
  document: 'document',
  documentEdit: 'documentEdit',
  documents: 'documents',
  dot: 'dot',
  download: 'download',
  editorAlignCenter: 'editor_align_center',
  editorAlignLeft: 'editor_align_left',
  editorAlignRight: 'editor_align_right',
  editorBold: 'editor_bold',
  editorCodeBlock: 'editor_code_block',
  editorComment: 'editor_comment',
  editorDistributeHorizontal: 'editorDistributeHorizontal',
  editorDistributeVertical: 'editorDistributeVertical',
  editorHeading: 'editor_heading',
  editorItalic: 'editor_italic',
  editorItemAlignLeft: 'editorItemAlignLeft',
  editorItemAlignBottom: 'editorItemAlignBottom',
  editorItemAlignCenter: 'editorItemAlignCenter',
  editorItemAlignMiddle: 'editorItemAlignMiddle',
  editorItemAlignRight: 'editorItemAlignRight',
  editorItemAlignTop: 'editorItemAlignTop',
  editorLink: 'editor_link',
  editorOrderedList: 'editor_ordered_list',
  editorPositionBottomLeft: 'editorPositionBottomLeft',
  editorPositionBottomRight: 'editorPositionBottomRight',
  editorPositionTopLeft: 'editorPositionTopLeft',
  editorPositionTopRight: 'editorPositionTopRight',
  editorRedo: 'editor_redo',
  editorStrike: 'editor_strike',
  editorTable: 'editor_table',
  editorUnderline: 'editor_underline',
  editorUndo: 'editor_undo',
  editorUnorderedList: 'editor_unordered_list',
  email: 'email',
  empty: 'empty',
  emsApp: 'app_ems',
  exit: 'exit',
  expand: 'expand',
  expandMini: 'expandMini',
  exportAction: 'export',
  eye: 'eye',
  eyeClosed: 'eye_closed',
  faceHappy: 'face_happy',
  faceNeutral: 'faceNeutral',
  faceSad: 'face_sad',
  filebeatApp: 'app_filebeat',
  filter: 'filter',
  flag: 'flag',
  folderCheck: 'folder_check',
  folderClosed: 'folder_closed',
  folderExclamation: 'folder_exclamation',
  folderOpen: 'folder_open',
  fullScreen: 'full_screen',
  gear: 'gear',
  gisApp: 'app_gis',
  glasses: 'glasses',
  globe: 'globe',
  grab: 'grab',
  grabHorizontal: 'grab_horizontal',
  graphApp: 'app_graph',
  grid: 'grid',
  grokApp: 'app_grok',
  heart: 'heart',
  heartbeatApp: 'app_heartbeat',
  heatmap: 'heatmap',
  help: 'help',
  home: 'home',
  iInCircle: 'iInCircle',
  image: 'image',
  importAction: 'import',
  indexClose: 'index_close',
  indexEdit: 'index_edit',
  indexFlush: 'index_flush',
  indexManagementApp: 'app_index_management',
  indexMapping: 'index_mapping',
  indexOpen: 'index_open',
  indexPatternApp: 'app_index_pattern',
  indexRollupApp: 'app_index_rollup',
  indexSettings: 'index_settings',
  inputOutput: 'inputOutput',
  inspect: 'inspect',
  invert: 'invert',
  ip: 'ip',
  keyboardShortcut: 'keyboard_shortcut',
  kqlField: 'kql_field',
  kqlFunction: 'kql_function',
  kqlOperand: 'kql_operand',
  kqlSelector: 'kql_selector',
  kqlValue: 'kql_value',
  lensApp: 'app_lens',
  link: 'link',
  list: 'list',
  listAdd: 'list_add',
  lock: 'lock',
  lockOpen: 'lockOpen',
  logsApp: 'app_logs',
  logoAerospike: 'logo_aerospike',
  logoApache: 'logo_apache',
  logoAPM: 'logo_apm',
  logoAppSearch: 'logo_app_search',
  logoAWS: 'logo_aws',
  logoAWSMono: 'logo_aws_mono',
  logoAzure: 'logo_azure',
  logoAzureMono: 'logo_azure_mono',
  logoBeats: 'logo_beats',
  logoBusinessAnalytics: 'logo_business_analytics',
  logoCeph: 'logo_ceph',
  logoCloud: 'logo_cloud',
  logoCloudEnterprise: 'logo_cloud_ece',
  logoCode: 'logo_code',
  logoCodesandbox: 'logo_codesandbox',
  logoCouchbase: 'logo_couchbase',
  logoDocker: 'logo_docker',
  logoDropwizard: 'logo_dropwizard',
  logoElastic: 'logo_elastic',
  logoElasticsearch: 'logo_elasticsearch',
  logoElasticStack: 'logo_elastic_stack',
  logoEnterpriseSearch: 'logo_enterprise_search',
  logoEtcd: 'logo_etcd',
  logoGCP: 'logo_gcp',
  logoGCPMono: 'logo_gcp_mono',
  logoGithub: 'logo_github',
  logoGmail: 'logo_gmail',
  logoGolang: 'logo_golang',
  logoGoogleG: 'logo_google_g',
  logoHAproxy: 'logo_haproxy',
  logoIBM: 'logo_ibm',
  logoIBMMono: 'logo_ibm_mono',
  logoKafka: 'logo_kafka',
  logoKibana: 'logo_kibana',
  logoKubernetes: 'logo_kubernetes',
  logoLogging: 'logo_logging',
  logoLogstash: 'logo_logstash',
  logoMaps: 'logo_maps',
  logoMemcached: 'logo_memcached',
  logoMetrics: 'logo_metrics',
  logoMongodb: 'logo_mongodb',
  logoMySQL: 'logo_mysql',
  logoNginx: 'logo_nginx',
  logoObservability: 'logo_observability',
  logoOsquery: 'logo_osquery',
  logoPhp: 'logo_php',
  logoPostgres: 'logo_postgres',
  logoPrometheus: 'logo_prometheus',
  logoRabbitmq: 'logo_rabbitmq',
  logoRedis: 'logo_redis',
  logoSecurity: 'logo_security',
  logoSiteSearch: 'logo_site_search',
  logoSketch: 'logo_sketch',
  logoSlack: 'logo_slack',
  logoUptime: 'logo_uptime',
  logoWebhook: 'logo_webhook',
  logoWindows: 'logo_windows',
  logoWorkplaceSearch: 'logo_workplace_search',
  logstashFilter: 'logstash_filter',
  logstashIf: 'logstash_if',
  logstashInput: 'logstash_input',
  logstashOutput: 'logstash_output',
  logstashQueue: 'logstash_queue',
  machineLearningApp: 'app_ml',
  magnet: 'magnet',
  magnifyWithMinus: 'magnifyWithMinus',
  magnifyWithPlus: 'magnifyWithPlus',
  managementApp: 'app_management',
  mapMarker: 'map_marker',
  memory: 'memory',
  menu: 'menu',
  menuLeft: 'menuLeft',
  menuRight: 'menuRight',
  merge: 'merge',
  metricbeatApp: 'app_metricbeat',
  metricsApp: 'app_metrics',
  minimize: 'minimize',
  minusInCircle: 'minus_in_circle',
  minusInCircleFilled: 'minus_in_circle_filled',
  monitoringApp: 'app_monitoring',
  moon: 'moon',
  nested: 'nested',
  node: 'node',
  notebookApp: 'app_notebook',
  number: 'number',
  offline: 'offline',
  online: 'online',
  package: 'package',
  packetbeatApp: 'app_packetbeat',
  pageSelect: 'pageSelect',
  pagesSelect: 'pagesSelect',
  partial: 'partial',
  paperClip: 'paper_clip',
  pause: 'pause',
  pencil: 'pencil',
  pin: 'pin',
  pinFilled: 'pin_filled',
  pipelineApp: 'app_pipeline',
  play: 'play',
  plusInCircle: 'plus_in_circle',
  plusInCircleFilled: 'plus_in_circle_filled',
  popout: 'popout',
  push: 'push',
  questionInCircle: 'question_in_circle',
  quote: 'quote',
  recentlyViewedApp: 'app_recently_viewed',
  refresh: 'refresh',
  reporter: 'reporter',
  reportingApp: 'app_reporting',
  save: 'save',
  savedObjectsApp: 'app_saved_objects',
  scale: 'scale',
  search: 'search',
  searchProfilerApp: 'app_search_profiler',
  securityAnalyticsApp: 'app_security_analytics',
  securityApp: 'app_security',
  securitySignal: 'securitySignal',
  securitySignalDetected: 'securitySignalDetected',
  securitySignalResolved: 'securitySignalResolved',
  shard: 'shard',
  share: 'share',
  snowflake: 'snowflake',
  sortable: 'sortable',
  sortDown: 'sort_down',
  sortLeft: 'sortLeft',
  sortRight: 'sortRight',
  sortUp: 'sort_up',
  spacesApp: 'app_spaces',
  sqlApp: 'app_sql',
  starEmpty: 'star_empty',
  starEmptySpace: 'star_empty_space',
  starFilled: 'star_filled',
  starFilledSpace: 'star_filled_space',
  starMinusEmpty: 'star_minus_empty',
  starMinusFilled: 'star_minus_filled',
  starPlusEmpty: 'starPlusEmpty',
  starPlusFilled: 'starPlusFilled',
  stats: 'stats',
  stop: 'stop',
  stopFilled: 'stop_filled',
  stopSlash: 'stop_slash',
  storage: 'storage',
  string: 'string',
  submodule: 'submodule',
  swatchInput: 'swatch_input',
  // Undocumented on purpose. Has an extra stroke for EuiColorPicker
  symlink: 'symlink',
  tableOfContents: 'tableOfContents',
  tableDensityExpanded: 'table_density_expanded',
  tableDensityCompact: 'table_density_compact',
  tableDensityNormal: 'table_density_normal',
  tag: 'tag',
  tear: 'tear',
  temperature: 'temperature',
  timeline: 'timeline',
  timelionApp: 'app_timelion',
  training: 'training',
  trash: 'trash',
  upgradeAssistantApp: 'app_upgrade_assistant',
  uptimeApp: 'app_uptime',
  user: 'user',
  users: 'users',
  usersRolesApp: 'app_users_roles',
  vector: 'vector',
  videoPlayer: 'videoPlayer',
  visArea: 'vis_area',
  visAreaStacked: 'vis_area_stacked',
  visBarHorizontal: 'vis_bar_horizontal',
  visBarHorizontalStacked: 'vis_bar_horizontal_stacked',
  visBarVertical: 'vis_bar_vertical',
  visBarVerticalStacked: 'vis_bar_vertical_stacked',
  visGauge: 'vis_gauge',
  visGoal: 'vis_goal',
  visLine: 'vis_line',
  visMapCoordinate: 'vis_map_coordinate',
  visMapRegion: 'vis_map_region',
  visMetric: 'vis_metric',
  visPie: 'vis_pie',
  visTable: 'vis_table',
  visTagCloud: 'vis_tag_cloud',
  visText: 'vis_text',
  visTimelion: 'vis_timelion',
  visualizeApp: 'app_visualize',
  visVega: 'vis_vega',
  visVisualBuilder: 'vis_visual_builder',
  watchesApp: 'app_watches',
  wrench: 'wrench',
  // Token Icon Imports
  tokenClass: 'tokens/tokenClass',
  tokenProperty: 'tokens/tokenProperty',
  tokenEnum: 'tokens/tokenEnum',
  tokenVariable: 'tokens/tokenVariable',
  tokenMethod: 'tokens/tokenMethod',
  tokenAnnotation: 'tokens/tokenAnnotation',
  tokenException: 'tokens/tokenException',
  tokenInterface: 'tokens/tokenInterface',
  tokenParameter: 'tokens/tokenParameter',
  tokenField: 'tokens/tokenField',
  tokenElement: 'tokens/tokenElement',
  tokenFunction: 'tokens/tokenFunction',
  tokenBoolean: 'tokens/tokenBoolean',
  tokenString: 'tokens/tokenString',
  tokenArray: 'tokens/tokenArray',
  tokenNumber: 'tokens/tokenNumber',
  tokenConstant: 'tokens/tokenConstant',
  tokenObject: 'tokens/tokenObject',
  tokenEvent: 'tokens/tokenEvent',
  tokenKey: 'tokens/tokenKey',
  tokenNull: 'tokens/tokenNull',
  tokenStruct: 'tokens/tokenStruct',
  tokenPackage: 'tokens/tokenPackage',
  tokenOperator: 'tokens/tokenOperator',
  tokenEnumMember: 'tokens/tokenEnumMember',
  tokenRepo: 'tokens/tokenRepo',
  tokenSymbol: 'tokens/tokenSymbol',
  tokenFile: 'tokens/tokenFile',
  tokenModule: 'tokens/tokenModule',
  tokenNamespace: 'tokens/tokenNamespace',
  tokenDate: 'tokens/tokenDate',
  tokenIP: 'tokens/tokenIP',
  tokenNested: 'tokens/tokenNested',
  tokenAlias: 'tokens/tokenAlias',
  tokenShape: 'tokens/tokenShape',
  tokenGeo: 'tokens/tokenGeo',
  tokenRange: 'tokens/tokenRange',
  tokenBinary: 'tokens/tokenBinary',
  tokenJoin: 'tokens/tokenJoin',
  tokenPercolator: 'tokens/tokenPercolator',
  tokenFlattened: 'tokens/tokenFlattened',
  tokenRankFeature: 'tokens/tokenRankFeature',
  tokenRankFeatures: 'tokens/tokenRankFeatures',
  tokenKeyword: 'tokens/tokenKeyword',
  tokenCompletionSuggester: 'tokens/tokenCompletionSuggester',
  tokenDenseVector: 'tokens/tokenDenseVector',
  tokenText: 'tokens/tokenText',
  tokenTokenCount: 'tokens/tokenTokenCount',
  tokenSearchType: 'tokens/tokenSearchType',
  tokenHistogram: 'tokens/tokenHistogram'
};
export var TYPES = keysOf(typeToPathMap);
var colorToClassMap = {
  default: null,
  primary: 'euiIcon--primary',
  secondary: 'euiIcon--secondary',
  success: 'euiIcon--success',
  accent: 'euiIcon--accent',
  warning: 'euiIcon--warning',
  danger: 'euiIcon--danger',
  text: 'euiIcon--text',
  subdued: 'euiIcon--subdued',
  ghost: 'euiIcon--ghost'
};
export var COLORS = keysOf(colorToClassMap);

function isNamedColor(name) {
  return colorToClassMap.hasOwnProperty(name);
} // We accept arbitrary color strings, which are impossible to type.


var sizeToClassNameMap = {
  original: null,
  s: 'euiIcon--small',
  m: 'euiIcon--medium',
  l: 'euiIcon--large',
  xl: 'euiIcon--xLarge',
  xxl: 'euiIcon--xxLarge'
};
export var SIZES = keysOf(sizeToClassNameMap);

function isEuiIconType(x) {
  return typeof x === 'string' && typeToPathMap.hasOwnProperty(x);
}

function getInitialIcon(icon) {
  if (icon == null) {
    return undefined;
  }

  if (isEuiIconType(icon)) {
    if (iconComponentCache.hasOwnProperty(icon)) {
      return iconComponentCache[icon];
    }

    return undefined;
  }

  return icon;
}

var generateId = htmlIdGenerator();
var iconComponentCache = {};
export var clearIconComponentCache = function clearIconComponentCache(icon) {
  if (icon != null) {
    delete iconComponentCache[icon];
  } else {
    iconComponentCache = {};
  }
};
export class EuiIcon extends PureComponent {
  isMounted = true;

  constructor(props) {
    super(props);
    const {
      type
    } = props;
    const initialIcon = getInitialIcon(type);
    let isLoading = false;

    if (isEuiIconType(type) && initialIcon == null) {
      isLoading = true;
      this.loadIconComponent(type);
    } else {
      this.onIconLoad();
    }

    this.state = {
      icon: initialIcon,
      iconTitle: undefined,
      isLoading,
      neededLoading: isLoading
    };
  }

  componentDidUpdate(prevProps) {
    const {
      type
    } = this.props;

    if (type !== prevProps.type) {
      if (isEuiIconType(type)) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          neededLoading: iconComponentCache.hasOwnProperty(type),
          isLoading: true
        });
        this.loadIconComponent(type);
      } else {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          icon: type,
          neededLoading: true,
          isLoading: false
        });
      }
    }
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  loadIconComponent = iconType => {
    if (iconComponentCache.hasOwnProperty(iconType)) {
      // exists in cache
      this.setState({
        isLoading: false,
        neededLoading: false,
        icon: iconComponentCache[iconType]
      });
      this.onIconLoad();
      return;
    }

    import(
    /* webpackChunkName: "icon.[request]" */
    // It's important that we don't use a template string here, it
    // stops webpack from building a dynamic require context.
    // eslint-disable-next-line prefer-template
    './assets/' + typeToPathMap[iconType] + '.js').then(({
      icon
    }) => {
      iconComponentCache[iconType] = icon;
      enqueueStateChange(() => {
        if (this.isMounted && this.props.type === iconType) {
          this.setState({
            icon,
            iconTitle: iconType,
            isLoading: false
          }, this.onIconLoad);
        }
      });
    });
  };
  onIconLoad = () => {
    const {
      onIconLoad
    } = this.props;

    if (onIconLoad) {
      onIconLoad();
    }
  };

  render() {
    const {
      type,
      size = 'm',
      color,
      className,
      tabIndex,
      title,
      onIconLoad,
      ...rest
    } = this.props;
    const {
      isLoading,
      neededLoading
    } = this.state;
    let optionalColorClass = null;
    let optionalCustomStyles = null;

    if (color) {
      if (isNamedColor(color)) {
        optionalColorClass = colorToClassMap[color];
      } else {
        optionalCustomStyles = {
          fill: color
        };
      }
    } // These icons are a little special and get some extra CSS flexibility


    const isAppIcon = type && typeof type === 'string' && (/.+App$/.test(type) || /.+Job$/.test(type) || type === 'dataVisualizer');
    const classes = classNames('euiIcon', sizeToClassNameMap[size], optionalColorClass, {
      'euiIcon--app': isAppIcon,
      'euiIcon-isLoading': isLoading,
      'euiIcon-isLoaded': !isLoading && neededLoading
    }, className);
    const icon = this.state.icon || empty; // This is a fix for IE and Edge, which ignores tabindex="-1" on an SVG, but respects
    // focusable="false".
    //   - If there's no tab index specified, we'll default the icon to not be focusable,
    //     which is how SVGs behave in Chrome, Safari, and FF.
    //   - If tab index is -1, then the consumer wants the icon to not be focusable.
    //   - For all other values, the consumer wants the icon to be focusable.

    const focusable = tabIndex == null || tabIndex === -1 ? 'false' : 'true';

    if (typeof icon === 'string') {
      return <img alt={title ? title : ''} src={icon} className={classes} tabIndex={tabIndex} {...rest} />;
    } else {
      const Svg = icon; // If it's an empty icon, or if there is no aria-label, aria-labelledby, or title it gets aria-hidden true

      const isAriaHidden = icon === empty || !(this.props['aria-label'] || this.props['aria-labelledby'] || this.props.title);
      const hideIconEmpty = isAriaHidden && {
        'aria-hidden': true
      };
      let titleId; // If no aria-label or aria-labelledby is provided but there's a title, a titleId is generated
      //  The svg aria-labelledby attribute gets this titleId
      //  The svg title element gets this titleId as an id

      if (!this.props['aria-label'] && !this.props['aria-labelledby'] && title) {
        titleId = {
          titleId: generateId()
        };
      }

      return <Svg className={classes} style={optionalCustomStyles} tabIndex={tabIndex} focusable={focusable} role="img" title={title} {...titleId} {...rest} {...hideIconEmpty} />;
    }
  }

}
EuiIcon.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
       * `Enum` is any of the named icons listed in the docs, `string` is usually a URL to an SVG file, and `elementType` is any React SVG component
       */
  type: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "annotation", "apmApp", "apmTrace", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "check", "checkInCircleFilled", "cheer", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "compute", "console", "consoleApp", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "currency", "cut", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentEdit", "documents", "dot", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "fullScreen", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "lensApp", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAPM", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuLeft", "menuRight", "merge", "metricbeatApp", "metricsApp", "minimize", "minusInCircle", "minusInCircleFilled", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "pin", "pinFilled", "pipelineApp", "play", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "reporter", "reportingApp", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "timeline", "timelionApp", "training", "trash", "upgradeAssistantApp", "uptimeApp", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,

  /**
       * One of EUI's color palette or a valid CSS color value https://developer.mozilla.org/en-US/docs/Web/CSS/color_value.
       * Note that coloring only works if your SVG is removed of fill attributes.
       */
  color: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.oneOf(["default", "primary", "secondary", "success", "accent", "warning", "danger", "text", "subdued", "ghost"]).isRequired]),

  /**
       * Note that every size other than `original` assumes the provided SVG sits on a square viewbox.
       */
  size: PropTypes.oneOf(["original", "s", "m", "l", "xl", "xxl"]),

  /**
       * Descriptive title for naming the icon based on its use
       */
  title: PropTypes.string,

  /**
       * A unique identifier for the title element
       */
  titleId: PropTypes.string,

  /**
       * Its value should be one or more element IDs
       */
  "aria-labelledby": PropTypes.string,

  /**
       * Callback when the icon has been loaded & rendered
       */
  onIconLoad: PropTypes.func
};

try {
  // @ts-ignore
  clearIconComponentCache.displayName = "clearIconComponentCache"; // @ts-ignore

  clearIconComponentCache.__docgenInfo = {
    "description": "",
    "displayName": "clearIconComponentCache",
    "props": {
      "toString": {
        "defaultValue": null,
        "description": "Returns a string representation of a string.",
        "name": "toString",
        "required": false,
        "type": {
          "name": "() => string"
        }
      },
      "charAt": {
        "defaultValue": null,
        "description": "Returns the character at the specified index.\n@param pos The zero-based index of the desired character.",
        "name": "charAt",
        "required": true,
        "type": {
          "name": "(pos: number) => string"
        }
      },
      "charCodeAt": {
        "defaultValue": null,
        "description": "Returns the Unicode value of the character at the specified location.\n@param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.",
        "name": "charCodeAt",
        "required": true,
        "type": {
          "name": "(index: number) => number"
        }
      },
      "concat": {
        "defaultValue": null,
        "description": "Returns a string that contains the concatenation of two or more strings.\n@param strings The strings to append to the end of the string.",
        "name": "concat",
        "required": true,
        "type": {
          "name": "(...strings: string[]) => string"
        }
      },
      "indexOf": {
        "defaultValue": null,
        "description": "Returns the position of the first occurrence of a substring.\n@param searchString The substring to search for in the string\n@param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.",
        "name": "indexOf",
        "required": true,
        "type": {
          "name": "(searchString: string, position?: number | undefined) => number"
        }
      },
      "lastIndexOf": {
        "defaultValue": null,
        "description": "Returns the last occurrence of a substring in the string.\n@param searchString The substring to search for.\n@param position The index at which to begin searching. If omitted, the search begins at the end of the string.",
        "name": "lastIndexOf",
        "required": true,
        "type": {
          "name": "(searchString: string, position?: number | undefined) => number"
        }
      },
      "localeCompare": {
        "defaultValue": null,
        "description": "Determines whether two strings are equivalent in the current locale.\nDetermines whether two strings are equivalent in the current or specified locale.\n@param that String to compare to target string\n@param that String to compare to target string\n@param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used. This parameter must conform to BCP 47 standards; see the Intl.Collator object for details.\n@param options An object that contains one or more properties that specify comparison options. see the Intl.Collator object for details.",
        "name": "localeCompare",
        "required": true,
        "type": {
          "name": "{ (that: string): number; (that: string, locales?: string | string[] | undefined, options?: CollatorOptions | undefined): number; }"
        }
      },
      "match": {
        "defaultValue": null,
        "description": "Matches a string with a regular expression, and returns an array containing the results of that search.\nMatches a string an object that supports being matched against, and returns an array containing the results of that search.\n@param regexp A variable name or string literal containing the regular expression pattern and flags.\n@param matcher An object that supports being matched against.",
        "name": "match",
        "required": true,
        "type": {
          "name": "{ (regexp: string | RegExp): RegExpMatchArray | null; (matcher: { [Symbol.match](string: string): RegExpMatchArray | null; }): RegExpMatchArray | null; }"
        }
      },
      "replace": {
        "defaultValue": null,
        "description": "Replaces text in a string, using a regular expression or search string.\nReplaces text in a string, using a regular expression or search string.\nReplaces text in a string, using an object that supports replacement within a string.\nReplaces text in a string, using an object that supports replacement within a string.\n@param searchValue A string to search for.\n@param replaceValue A string containing the text to replace for every successful match of searchValue in this string.\n@param searchValue A string to search for.\n@param replacer A function that returns the replacement text.\n@param searchValue A object can search for and replace matches within a string.\n@param replaceValue A string containing the text to replace for every successful match of searchValue in this string.\n@param searchValue A object can search for and replace matches within a string.\n@param replacer A function that returns the replacement text.",
        "name": "replace",
        "required": true,
        "type": {
          "name": "{ (searchValue: string | RegExp, replaceValue: string): string; (searchValue: string | RegExp, replacer: (substring: string, ...args: any[]) => string): string; (searchValue: { ...; }, replaceValue: string): string; (searchValue: { ...; }, replacer: (substring: string, ...args: any[]) => string): string; }"
        }
      },
      "search": {
        "defaultValue": null,
        "description": "Finds the first substring match in a regular expression search.\nFinds the first substring match in a regular expression search.\n@param regexp The regular expression pattern and applicable flags.\n@param searcher An object which supports searching within a string.",
        "name": "search",
        "required": true,
        "type": {
          "name": "{ (regexp: string | RegExp): number; (searcher: { [Symbol.search](string: string): number; }): number; }"
        }
      },
      "slice": {
        "defaultValue": null,
        "description": "Returns a section of a string.\n@param start The index to the beginning of the specified portion of stringObj.\n@param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.\rIf this value is not specified, the substring continues to the end of stringObj.",
        "name": "slice",
        "required": true,
        "type": {
          "name": "(start?: number | undefined, end?: number | undefined) => string"
        }
      },
      "split": {
        "defaultValue": null,
        "description": "Split a string into substrings using the specified separator and return them as an array.\nSplit a string into substrings using the specified separator and return them as an array.\n@param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.\n@param limit A value used to limit the number of elements returned in the array.\n@param splitter An object that can split a string.\n@param limit A value used to limit the number of elements returned in the array.",
        "name": "split",
        "required": true,
        "type": {
          "name": "{ (separator: string | RegExp, limit?: number | undefined): string[]; (splitter: { [Symbol.split](string: string, limit?: number | undefined): string[]; }, limit?: number | undefined): string[]; }"
        }
      },
      "substring": {
        "defaultValue": null,
        "description": "Returns the substring at the specified location within a String object.\n@param start The zero-based index number indicating the beginning of the substring.\n@param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.\rIf end is omitted, the characters from start through the end of the original string are returned.",
        "name": "substring",
        "required": true,
        "type": {
          "name": "(start: number, end?: number | undefined) => string"
        }
      },
      "toLowerCase": {
        "defaultValue": null,
        "description": "Converts all the alphabetic characters in a string to lowercase.",
        "name": "toLowerCase",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "toLocaleLowerCase": {
        "defaultValue": null,
        "description": "Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.",
        "name": "toLocaleLowerCase",
        "required": true,
        "type": {
          "name": "(locales?: string | string[] | undefined) => string"
        }
      },
      "toUpperCase": {
        "defaultValue": null,
        "description": "Converts all the alphabetic characters in a string to uppercase.",
        "name": "toUpperCase",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "toLocaleUpperCase": {
        "defaultValue": null,
        "description": "Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.",
        "name": "toLocaleUpperCase",
        "required": true,
        "type": {
          "name": "(locales?: string | string[] | undefined) => string"
        }
      },
      "trim": {
        "defaultValue": null,
        "description": "Removes the leading and trailing white space and line terminator characters from a string.",
        "name": "trim",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "length": {
        "defaultValue": null,
        "description": "Returns the length of a String object.",
        "name": "length",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "substr": {
        "defaultValue": null,
        "description": "Gets a substring beginning at the specified location and having the specified length.\n@param from The starting position of the desired substring. The index of the first character in the string is zero.\n@param length The number of characters to include in the returned substring.",
        "name": "substr",
        "required": true,
        "type": {
          "name": "(from: number, length?: number | undefined) => string"
        }
      },
      "valueOf": {
        "defaultValue": null,
        "description": "Returns the primitive value of the specified object.",
        "name": "valueOf",
        "required": false,
        "type": {
          "name": "() => string"
        }
      },
      "codePointAt": {
        "defaultValue": null,
        "description": "Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point\nvalue of the UTF-16 encoded code point starting at the string element at position pos in\r\nthe String resulting from converting this object to a String.\r\nIf there is no element at that position, the result is undefined.\r\nIf a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.",
        "name": "codePointAt",
        "required": true,
        "type": {
          "name": "(pos: number) => number | undefined"
        }
      },
      "includes": {
        "defaultValue": null,
        "description": "Returns true if searchString appears as a substring of the result of converting this\nobject to a String, at one or more positions that are\r\ngreater than or equal to position; otherwise, returns false.\n@param searchString search string\n@param position If position is undefined, 0 is assumed, so as to search all of the String.",
        "name": "includes",
        "required": true,
        "type": {
          "name": "(searchString: string, position?: number | undefined) => boolean"
        }
      },
      "endsWith": {
        "defaultValue": null,
        "description": "Returns true if the sequence of elements of searchString converted to a String is the\nsame as the corresponding elements of this object (converted to a String) starting at\r\nendPosition \u2013 length(this). Otherwise returns false.",
        "name": "endsWith",
        "required": true,
        "type": {
          "name": "(searchString: string, endPosition?: number | undefined) => boolean"
        }
      },
      "normalize": {
        "defaultValue": null,
        "description": "Returns the String value result of normalizing the string into the normalization form\nnamed by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.\nReturns the String value result of normalizing the string into the normalization form\r\nnamed by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.\n@param form Applicable values: \"NFC\", \"NFD\", \"NFKC\", or \"NFKD\", If not specified default\ris \"NFC\"\n@param form Applicable values: \"NFC\", \"NFD\", \"NFKC\", or \"NFKD\", If not specified default\ris \"NFC\"",
        "name": "normalize",
        "required": true,
        "type": {
          "name": "{ (form: \"NFC\" | \"NFD\" | \"NFKC\" | \"NFKD\"): string; (form?: string | undefined): string; }"
        }
      },
      "repeat": {
        "defaultValue": null,
        "description": "Returns a String value that is made from count copies appended together. If count is 0,\nthe empty string is returned.\n@param count number of copies to append",
        "name": "repeat",
        "required": true,
        "type": {
          "name": "(count: number) => string"
        }
      },
      "startsWith": {
        "defaultValue": null,
        "description": "Returns true if the sequence of elements of searchString converted to a String is the\nsame as the corresponding elements of this object (converted to a String) starting at\r\nposition. Otherwise returns false.",
        "name": "startsWith",
        "required": true,
        "type": {
          "name": "(searchString: string, position?: number | undefined) => boolean"
        }
      },
      "anchor": {
        "defaultValue": null,
        "description": "Returns an <a> HTML anchor element and sets the name attribute to the text value\n@param name",
        "name": "anchor",
        "required": true,
        "type": {
          "name": "(name: string) => string"
        }
      },
      "big": {
        "defaultValue": null,
        "description": "Returns a <big> HTML element",
        "name": "big",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "blink": {
        "defaultValue": null,
        "description": "Returns a <blink> HTML element",
        "name": "blink",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "bold": {
        "defaultValue": null,
        "description": "Returns a <b> HTML element",
        "name": "bold",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "fixed": {
        "defaultValue": null,
        "description": "Returns a <tt> HTML element",
        "name": "fixed",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "fontcolor": {
        "defaultValue": null,
        "description": "Returns a <font> HTML element and sets the color attribute value",
        "name": "fontcolor",
        "required": true,
        "type": {
          "name": "(color: string) => string"
        }
      },
      "fontsize": {
        "defaultValue": null,
        "description": "Returns a <font> HTML element and sets the size attribute value\nReturns a <font> HTML element and sets the size attribute value",
        "name": "fontsize",
        "required": true,
        "type": {
          "name": "{ (size: number): string; (size: string): string; }"
        }
      },
      "italics": {
        "defaultValue": null,
        "description": "Returns an <i> HTML element",
        "name": "italics",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "link": {
        "defaultValue": null,
        "description": "Returns an <a> HTML element and sets the href attribute value",
        "name": "link",
        "required": true,
        "type": {
          "name": "(url: string) => string"
        }
      },
      "small": {
        "defaultValue": null,
        "description": "Returns a <small> HTML element",
        "name": "small",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "strike": {
        "defaultValue": null,
        "description": "Returns a <strike> HTML element",
        "name": "strike",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "sub": {
        "defaultValue": null,
        "description": "Returns a <sub> HTML element",
        "name": "sub",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "sup": {
        "defaultValue": null,
        "description": "Returns a <sup> HTML element",
        "name": "sup",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "__@iterator": {
        "defaultValue": null,
        "description": "Iterator",
        "name": "__@iterator",
        "required": true,
        "type": {
          "name": "() => IterableIterator<string>"
        }
      },
      "padStart": {
        "defaultValue": null,
        "description": "Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.\nThe padding is applied from the start (left) of the current string.\n@param maxLength The length of the resulting string once the current string has been padded.\rIf this parameter is smaller than the current string's length, the current string will be returned as it is.\n@param fillString The string to pad the current string with.\rIf this string is too long, it will be truncated and the left-most part will be applied.\rThe default value for this parameter is \" \" (U+0020).",
        "name": "padStart",
        "required": true,
        "type": {
          "name": "(maxLength: number, fillString?: string | undefined) => string"
        }
      },
      "padEnd": {
        "defaultValue": null,
        "description": "Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.\nThe padding is applied from the end (right) of the current string.\n@param maxLength The length of the resulting string once the current string has been padded.\rIf this parameter is smaller than the current string's length, the current string will be returned as it is.\n@param fillString The string to pad the current string with.\rIf this string is too long, it will be truncated and the left-most part will be applied.\rThe default value for this parameter is \" \" (U+0020).",
        "name": "padEnd",
        "required": true,
        "type": {
          "name": "(maxLength: number, fillString?: string | undefined) => string"
        }
      },
      "trimEnd": {
        "defaultValue": null,
        "description": "Removes the trailing white space and line terminator characters from a string.",
        "name": "trimEnd",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "trimStart": {
        "defaultValue": null,
        "description": "Removes the leading white space and line terminator characters from a string.",
        "name": "trimStart",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "trimLeft": {
        "defaultValue": null,
        "description": "Removes the leading white space and line terminator characters from a string.\nRemoves whitespace from the left end of a string.",
        "name": "trimLeft",
        "required": true,
        "type": {
          "name": "{ (): string; (): string; }"
        }
      },
      "trimRight": {
        "defaultValue": null,
        "description": "Removes the trailing white space and line terminator characters from a string.\nRemoves whitespace from the right end of a string.",
        "name": "trimRight",
        "required": true,
        "type": {
          "name": "{ (): string; (): string; }"
        }
      },
      "matchAll": {
        "defaultValue": null,
        "description": "Matches a string with a regular expression, and returns an iterable of matches\ncontaining the results of that search.\n@param regexp A variable name or string literal containing the regular expression pattern and flags.",
        "name": "matchAll",
        "required": true,
        "type": {
          "name": "(regexp: RegExp) => IterableIterator<RegExpMatchArray>"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/icon/icon.tsx#clearIconComponentCache"] = {
      docgenInfo: clearIconComponentCache.__docgenInfo,
      name: "clearIconComponentCache",
      path: "src/components/icon/icon.tsx#clearIconComponentCache"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  EuiIcon.displayName = "EuiIcon"; // @ts-ignore

  EuiIcon.__docgenInfo = {
    "description": "",
    "displayName": "EuiIcon",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "Defines a string value that labels the current element.\n@see aria-labelledby.",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "string": {
        "defaultValue": null,
        "description": "",
        "name": "string",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "id": {
        "defaultValue": null,
        "description": "",
        "name": "id",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "lang": {
        "defaultValue": null,
        "description": "",
        "name": "lang",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "style": {
        "defaultValue": null,
        "description": "",
        "name": "style",
        "required": false,
        "type": {
          "name": "CSSProperties | undefined"
        }
      },
      "tabIndex": {
        "defaultValue": null,
        "description": "",
        "name": "tabIndex",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "role": {
        "defaultValue": null,
        "description": "",
        "name": "role",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-activedescendant": {
        "defaultValue": null,
        "description": "Identifies the currently active element when DOM focus is on a composite widget, textbox, group, or application.",
        "name": "aria-activedescendant",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-atomic": {
        "defaultValue": null,
        "description": "Indicates whether assistive technologies will present all, or only parts of, the changed region based on the change notifications defined by the aria-relevant attribute.",
        "name": "aria-atomic",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-autocomplete": {
        "defaultValue": null,
        "description": "Indicates whether inputting text could trigger display of one or more predictions of the user's intended value for an input and specifies how predictions would be\npresented if they are made.",
        "name": "aria-autocomplete",
        "required": false,
        "type": {
          "name": "\"none\" | \"inline\" | \"list\" | \"both\" | undefined"
        }
      },
      "aria-busy": {
        "defaultValue": null,
        "description": "Indicates an element is being modified and that assistive technologies MAY want to wait until the modifications are complete before exposing them to the user.",
        "name": "aria-busy",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-checked": {
        "defaultValue": null,
        "description": "Indicates the current \"checked\" state of checkboxes, radio buttons, and other widgets.\n@see aria-pressed\n@see aria-selected.",
        "name": "aria-checked",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | \"mixed\" | undefined"
        }
      },
      "aria-colcount": {
        "defaultValue": null,
        "description": "Defines the total number of columns in a table, grid, or treegrid.\n@see aria-colindex.",
        "name": "aria-colcount",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-colindex": {
        "defaultValue": null,
        "description": "Defines an element's column index or position with respect to the total number of columns within a table, grid, or treegrid.\n@see aria-colcount\n@see aria-colspan.",
        "name": "aria-colindex",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-colspan": {
        "defaultValue": null,
        "description": "Defines the number of columns spanned by a cell or gridcell within a table, grid, or treegrid.\n@see aria-colindex\n@see aria-rowspan.",
        "name": "aria-colspan",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-controls": {
        "defaultValue": null,
        "description": "Identifies the element (or elements) whose contents or presence are controlled by the current element.\n@see aria-owns.",
        "name": "aria-controls",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-current": {
        "defaultValue": null,
        "description": "Indicates the element that represents the current item within a container or set of related elements.",
        "name": "aria-current",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | \"page\" | \"step\" | \"location\" | \"date\" | \"time\" | undefined"
        }
      },
      "aria-describedby": {
        "defaultValue": null,
        "description": "Identifies the element (or elements) that describes the object.\n@see aria-labelledby",
        "name": "aria-describedby",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-details": {
        "defaultValue": null,
        "description": "Identifies the element that provides a detailed, extended description for the object.\n@see aria-describedby.",
        "name": "aria-details",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-disabled": {
        "defaultValue": null,
        "description": "Indicates that the element is perceivable but disabled, so it is not editable or otherwise operable.\n@see aria-hidden\n@see aria-readonly.",
        "name": "aria-disabled",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-dropeffect": {
        "defaultValue": null,
        "description": "Indicates what functions can be performed when a dragged object is released on the drop target.\n@deprecated in ARIA 1.1",
        "name": "aria-dropeffect",
        "required": false,
        "type": {
          "name": "\"none\" | \"copy\" | \"execute\" | \"link\" | \"move\" | \"popup\" | undefined"
        }
      },
      "aria-errormessage": {
        "defaultValue": null,
        "description": "Identifies the element that provides an error message for the object.\n@see aria-invalid\n@see aria-describedby.",
        "name": "aria-errormessage",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-expanded": {
        "defaultValue": null,
        "description": "Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed.",
        "name": "aria-expanded",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-flowto": {
        "defaultValue": null,
        "description": "Identifies the next element (or elements) in an alternate reading order of content which, at the user's discretion,\nallows assistive technology to override the general default of reading in document source order.",
        "name": "aria-flowto",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-grabbed": {
        "defaultValue": null,
        "description": "Indicates an element's \"grabbed\" state in a drag-and-drop operation.\n@deprecated in ARIA 1.1",
        "name": "aria-grabbed",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-haspopup": {
        "defaultValue": null,
        "description": "Indicates the availability and type of interactive popup element, such as menu or dialog, that can be triggered by an element.",
        "name": "aria-haspopup",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | \"menu\" | \"listbox\" | \"tree\" | \"grid\" | \"dialog\" | undefined"
        }
      },
      "aria-hidden": {
        "defaultValue": null,
        "description": "Indicates whether the element is exposed to an accessibility API.\n@see aria-disabled.",
        "name": "aria-hidden",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-invalid": {
        "defaultValue": null,
        "description": "Indicates the entered value does not conform to the format expected by the application.\n@see aria-errormessage.",
        "name": "aria-invalid",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | \"grammar\" | \"spelling\" | undefined"
        }
      },
      "aria-keyshortcuts": {
        "defaultValue": null,
        "description": "Indicates keyboard shortcuts that an author has implemented to activate or give focus to an element.",
        "name": "aria-keyshortcuts",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-labelledby": {
        "defaultValue": null,
        "description": "Identifies the element (or elements) that labels the current element.\nIts value should be one or more element IDs\n@see aria-describedby.",
        "name": "aria-labelledby",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-level": {
        "defaultValue": null,
        "description": "Defines the hierarchical level of an element within a structure.",
        "name": "aria-level",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-live": {
        "defaultValue": null,
        "description": "Indicates that an element will be updated, and describes the types of updates the user agents, assistive technologies, and user can expect from the live region.",
        "name": "aria-live",
        "required": false,
        "type": {
          "name": "\"off\" | \"assertive\" | \"polite\" | undefined"
        }
      },
      "aria-modal": {
        "defaultValue": null,
        "description": "Indicates whether an element is modal when displayed.",
        "name": "aria-modal",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-multiline": {
        "defaultValue": null,
        "description": "Indicates whether a text box accepts multiple lines of input or only a single line.",
        "name": "aria-multiline",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-multiselectable": {
        "defaultValue": null,
        "description": "Indicates that the user may select more than one item from the current selectable descendants.",
        "name": "aria-multiselectable",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-orientation": {
        "defaultValue": null,
        "description": "Indicates whether the element's orientation is horizontal, vertical, or unknown/ambiguous.",
        "name": "aria-orientation",
        "required": false,
        "type": {
          "name": "\"horizontal\" | \"vertical\" | undefined"
        }
      },
      "aria-owns": {
        "defaultValue": null,
        "description": "Identifies an element (or elements) in order to define a visual, functional, or contextual parent/child relationship\nbetween DOM elements where the DOM hierarchy cannot be used to represent the relationship.\n@see aria-controls.",
        "name": "aria-owns",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-placeholder": {
        "defaultValue": null,
        "description": "Defines a short hint (a word or short phrase) intended to aid the user with data entry when the control has no value.\nA hint could be a sample value or a brief description of the expected format.",
        "name": "aria-placeholder",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-posinset": {
        "defaultValue": null,
        "description": "Defines an element's number or position in the current set of listitems or treeitems. Not required if all elements in the set are present in the DOM.\n@see aria-setsize.",
        "name": "aria-posinset",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-pressed": {
        "defaultValue": null,
        "description": "Indicates the current \"pressed\" state of toggle buttons.\n@see aria-checked\n@see aria-selected.",
        "name": "aria-pressed",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | \"mixed\" | undefined"
        }
      },
      "aria-readonly": {
        "defaultValue": null,
        "description": "Indicates that the element is not editable, but is otherwise operable.\n@see aria-disabled.",
        "name": "aria-readonly",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-relevant": {
        "defaultValue": null,
        "description": "Indicates what notifications the user agent will trigger when the accessibility tree within a live region is modified.\n@see aria-atomic.",
        "name": "aria-relevant",
        "required": false,
        "type": {
          "name": "\"text\" | \"additions\" | \"additions text\" | \"all\" | \"removals\" | undefined"
        }
      },
      "aria-required": {
        "defaultValue": null,
        "description": "Indicates that user input is required on the element before a form may be submitted.",
        "name": "aria-required",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-roledescription": {
        "defaultValue": null,
        "description": "Defines a human-readable, author-localized description for the role of an element.",
        "name": "aria-roledescription",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-rowcount": {
        "defaultValue": null,
        "description": "Defines the total number of rows in a table, grid, or treegrid.\n@see aria-rowindex.",
        "name": "aria-rowcount",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-rowindex": {
        "defaultValue": null,
        "description": "Defines an element's row index or position with respect to the total number of rows within a table, grid, or treegrid.\n@see aria-rowcount\n@see aria-rowspan.",
        "name": "aria-rowindex",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-rowspan": {
        "defaultValue": null,
        "description": "Defines the number of rows spanned by a cell or gridcell within a table, grid, or treegrid.\n@see aria-rowindex\n@see aria-colspan.",
        "name": "aria-rowspan",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-selected": {
        "defaultValue": null,
        "description": "Indicates the current \"selected\" state of various widgets.\n@see aria-checked\n@see aria-pressed.",
        "name": "aria-selected",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "aria-setsize": {
        "defaultValue": null,
        "description": "Defines the number of items in the current set of listitems or treeitems. Not required if all elements in the set are present in the DOM.\n@see aria-posinset.",
        "name": "aria-setsize",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-sort": {
        "defaultValue": null,
        "description": "Indicates if items in a table or grid are sorted in ascending or descending order.",
        "name": "aria-sort",
        "required": false,
        "type": {
          "name": "\"none\" | \"ascending\" | \"descending\" | \"other\" | undefined"
        }
      },
      "aria-valuemax": {
        "defaultValue": null,
        "description": "Defines the maximum allowed value for a range widget.",
        "name": "aria-valuemax",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-valuemin": {
        "defaultValue": null,
        "description": "Defines the minimum allowed value for a range widget.",
        "name": "aria-valuemin",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-valuenow": {
        "defaultValue": null,
        "description": "Defines the current value for a range widget.\n@see aria-valuetext.",
        "name": "aria-valuenow",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "aria-valuetext": {
        "defaultValue": null,
        "description": "Defines the human readable text alternative of aria-valuenow for a range widget.",
        "name": "aria-valuetext",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "dangerouslySetInnerHTML": {
        "defaultValue": null,
        "description": "",
        "name": "dangerouslySetInnerHTML",
        "required": false,
        "type": {
          "name": "{ __html: string; } | undefined"
        }
      },
      "onCopy": {
        "defaultValue": null,
        "description": "",
        "name": "onCopy",
        "required": false,
        "type": {
          "name": "((event: ClipboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCopyCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onCopyCapture",
        "required": false,
        "type": {
          "name": "((event: ClipboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCut": {
        "defaultValue": null,
        "description": "",
        "name": "onCut",
        "required": false,
        "type": {
          "name": "((event: ClipboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCutCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onCutCapture",
        "required": false,
        "type": {
          "name": "((event: ClipboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPaste": {
        "defaultValue": null,
        "description": "",
        "name": "onPaste",
        "required": false,
        "type": {
          "name": "((event: ClipboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPasteCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPasteCapture",
        "required": false,
        "type": {
          "name": "((event: ClipboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCompositionEnd": {
        "defaultValue": null,
        "description": "",
        "name": "onCompositionEnd",
        "required": false,
        "type": {
          "name": "((event: CompositionEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCompositionEndCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onCompositionEndCapture",
        "required": false,
        "type": {
          "name": "((event: CompositionEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCompositionStart": {
        "defaultValue": null,
        "description": "",
        "name": "onCompositionStart",
        "required": false,
        "type": {
          "name": "((event: CompositionEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCompositionStartCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onCompositionStartCapture",
        "required": false,
        "type": {
          "name": "((event: CompositionEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCompositionUpdate": {
        "defaultValue": null,
        "description": "",
        "name": "onCompositionUpdate",
        "required": false,
        "type": {
          "name": "((event: CompositionEvent<SVGElement>) => void) | undefined"
        }
      },
      "onCompositionUpdateCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onCompositionUpdateCapture",
        "required": false,
        "type": {
          "name": "((event: CompositionEvent<SVGElement>) => void) | undefined"
        }
      },
      "onFocus": {
        "defaultValue": null,
        "description": "",
        "name": "onFocus",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<SVGElement>) => void) | undefined"
        }
      },
      "onFocusCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onFocusCapture",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<SVGElement>) => void) | undefined"
        }
      },
      "onBlur": {
        "defaultValue": null,
        "description": "",
        "name": "onBlur",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<SVGElement>) => void) | undefined"
        }
      },
      "onBlurCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onBlurCapture",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<SVGElement>) => void) | undefined"
        }
      },
      "onChange": {
        "defaultValue": null,
        "description": "",
        "name": "onChange",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onChangeCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onChangeCapture",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onBeforeInput": {
        "defaultValue": null,
        "description": "",
        "name": "onBeforeInput",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onBeforeInputCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onBeforeInputCapture",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onInput": {
        "defaultValue": null,
        "description": "",
        "name": "onInput",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onInputCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onInputCapture",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onReset": {
        "defaultValue": null,
        "description": "",
        "name": "onReset",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onResetCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onResetCapture",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onSubmit": {
        "defaultValue": null,
        "description": "",
        "name": "onSubmit",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onSubmitCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onSubmitCapture",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onInvalid": {
        "defaultValue": null,
        "description": "",
        "name": "onInvalid",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onInvalidCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onInvalidCapture",
        "required": false,
        "type": {
          "name": "((event: FormEvent<SVGElement>) => void) | undefined"
        }
      },
      "onLoad": {
        "defaultValue": null,
        "description": "",
        "name": "onLoad",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onLoadCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onLoadCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onError": {
        "defaultValue": null,
        "description": "",
        "name": "onError",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onErrorCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onErrorCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onKeyDown": {
        "defaultValue": null,
        "description": "",
        "name": "onKeyDown",
        "required": false,
        "type": {
          "name": "((event: KeyboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onKeyDownCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onKeyDownCapture",
        "required": false,
        "type": {
          "name": "((event: KeyboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onKeyPress": {
        "defaultValue": null,
        "description": "",
        "name": "onKeyPress",
        "required": false,
        "type": {
          "name": "((event: KeyboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onKeyPressCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onKeyPressCapture",
        "required": false,
        "type": {
          "name": "((event: KeyboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onKeyUp": {
        "defaultValue": null,
        "description": "",
        "name": "onKeyUp",
        "required": false,
        "type": {
          "name": "((event: KeyboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onKeyUpCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onKeyUpCapture",
        "required": false,
        "type": {
          "name": "((event: KeyboardEvent<SVGElement>) => void) | undefined"
        }
      },
      "onAbort": {
        "defaultValue": null,
        "description": "",
        "name": "onAbort",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onAbortCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onAbortCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onCanPlay": {
        "defaultValue": null,
        "description": "",
        "name": "onCanPlay",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onCanPlayCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onCanPlayCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onCanPlayThrough": {
        "defaultValue": null,
        "description": "",
        "name": "onCanPlayThrough",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onCanPlayThroughCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onCanPlayThroughCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onDurationChange": {
        "defaultValue": null,
        "description": "",
        "name": "onDurationChange",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onDurationChangeCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDurationChangeCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onEmptied": {
        "defaultValue": null,
        "description": "",
        "name": "onEmptied",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onEmptiedCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onEmptiedCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onEncrypted": {
        "defaultValue": null,
        "description": "",
        "name": "onEncrypted",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onEncryptedCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onEncryptedCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onEnded": {
        "defaultValue": null,
        "description": "",
        "name": "onEnded",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onEndedCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onEndedCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onLoadedData": {
        "defaultValue": null,
        "description": "",
        "name": "onLoadedData",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onLoadedDataCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onLoadedDataCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onLoadedMetadata": {
        "defaultValue": null,
        "description": "",
        "name": "onLoadedMetadata",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onLoadedMetadataCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onLoadedMetadataCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onLoadStart": {
        "defaultValue": null,
        "description": "",
        "name": "onLoadStart",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onLoadStartCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onLoadStartCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onPause": {
        "defaultValue": null,
        "description": "",
        "name": "onPause",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onPauseCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPauseCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onPlay": {
        "defaultValue": null,
        "description": "",
        "name": "onPlay",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onPlayCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPlayCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onPlaying": {
        "defaultValue": null,
        "description": "",
        "name": "onPlaying",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onPlayingCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPlayingCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onProgress": {
        "defaultValue": null,
        "description": "",
        "name": "onProgress",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onProgressCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onProgressCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onRateChange": {
        "defaultValue": null,
        "description": "",
        "name": "onRateChange",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onRateChangeCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onRateChangeCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onSeeked": {
        "defaultValue": null,
        "description": "",
        "name": "onSeeked",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onSeekedCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onSeekedCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onSeeking": {
        "defaultValue": null,
        "description": "",
        "name": "onSeeking",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onSeekingCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onSeekingCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onStalled": {
        "defaultValue": null,
        "description": "",
        "name": "onStalled",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onStalledCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onStalledCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onSuspend": {
        "defaultValue": null,
        "description": "",
        "name": "onSuspend",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onSuspendCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onSuspendCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onTimeUpdate": {
        "defaultValue": null,
        "description": "",
        "name": "onTimeUpdate",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onTimeUpdateCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onTimeUpdateCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onVolumeChange": {
        "defaultValue": null,
        "description": "",
        "name": "onVolumeChange",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onVolumeChangeCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onVolumeChangeCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onWaiting": {
        "defaultValue": null,
        "description": "",
        "name": "onWaiting",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onWaitingCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onWaitingCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onAuxClick": {
        "defaultValue": null,
        "description": "",
        "name": "onAuxClick",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onAuxClickCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onAuxClickCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onClick": {
        "defaultValue": null,
        "description": "",
        "name": "onClick",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onClickCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onClickCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onContextMenu": {
        "defaultValue": null,
        "description": "",
        "name": "onContextMenu",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onContextMenuCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onContextMenuCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onDoubleClick": {
        "defaultValue": null,
        "description": "",
        "name": "onDoubleClick",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onDoubleClickCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDoubleClickCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onDrag": {
        "defaultValue": null,
        "description": "",
        "name": "onDrag",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDragCapture",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragEnd": {
        "defaultValue": null,
        "description": "",
        "name": "onDragEnd",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragEndCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDragEndCapture",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragEnter": {
        "defaultValue": null,
        "description": "",
        "name": "onDragEnter",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragEnterCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDragEnterCapture",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragExit": {
        "defaultValue": null,
        "description": "",
        "name": "onDragExit",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragExitCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDragExitCapture",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragLeave": {
        "defaultValue": null,
        "description": "",
        "name": "onDragLeave",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragLeaveCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDragLeaveCapture",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragOver": {
        "defaultValue": null,
        "description": "",
        "name": "onDragOver",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragOverCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDragOverCapture",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragStart": {
        "defaultValue": null,
        "description": "",
        "name": "onDragStart",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDragStartCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDragStartCapture",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDrop": {
        "defaultValue": null,
        "description": "",
        "name": "onDrop",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onDropCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onDropCapture",
        "required": false,
        "type": {
          "name": "((event: DragEvent<SVGElement>) => void) | undefined"
        }
      },
      "onMouseDown": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseDown",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseDownCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseDownCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseEnter": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseEnter",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseLeave": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseLeave",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseMove": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseMove",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseMoveCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseMoveCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseOut": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseOut",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseOutCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseOutCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseOver": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseOver",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseOverCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseOverCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseUp": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseUp",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onMouseUpCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onMouseUpCapture",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<SVGElement, MouseEvent>) => void) | undefined"
        }
      },
      "onSelect": {
        "defaultValue": null,
        "description": "",
        "name": "onSelect",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onSelectCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onSelectCapture",
        "required": false,
        "type": {
          "name": "((event: SyntheticEvent<SVGElement, Event>) => void) | undefined"
        }
      },
      "onTouchCancel": {
        "defaultValue": null,
        "description": "",
        "name": "onTouchCancel",
        "required": false,
        "type": {
          "name": "((event: TouchEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTouchCancelCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onTouchCancelCapture",
        "required": false,
        "type": {
          "name": "((event: TouchEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTouchEnd": {
        "defaultValue": null,
        "description": "",
        "name": "onTouchEnd",
        "required": false,
        "type": {
          "name": "((event: TouchEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTouchEndCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onTouchEndCapture",
        "required": false,
        "type": {
          "name": "((event: TouchEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTouchMove": {
        "defaultValue": null,
        "description": "",
        "name": "onTouchMove",
        "required": false,
        "type": {
          "name": "((event: TouchEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTouchMoveCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onTouchMoveCapture",
        "required": false,
        "type": {
          "name": "((event: TouchEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTouchStart": {
        "defaultValue": null,
        "description": "",
        "name": "onTouchStart",
        "required": false,
        "type": {
          "name": "((event: TouchEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTouchStartCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onTouchStartCapture",
        "required": false,
        "type": {
          "name": "((event: TouchEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerDown": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerDown",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerDownCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerDownCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerMove": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerMove",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerMoveCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerMoveCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerUp": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerUp",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerUpCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerUpCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerCancel": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerCancel",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerCancelCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerCancelCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerEnter": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerEnter",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerEnterCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerEnterCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerLeave": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerLeave",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerLeaveCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerLeaveCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerOver": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerOver",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerOverCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerOverCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerOut": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerOut",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onPointerOutCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onPointerOutCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onGotPointerCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onGotPointerCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onGotPointerCaptureCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onGotPointerCaptureCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onLostPointerCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onLostPointerCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onLostPointerCaptureCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onLostPointerCaptureCapture",
        "required": false,
        "type": {
          "name": "((event: PointerEvent<SVGElement>) => void) | undefined"
        }
      },
      "onScroll": {
        "defaultValue": null,
        "description": "",
        "name": "onScroll",
        "required": false,
        "type": {
          "name": "((event: UIEvent<SVGElement, UIEvent>) => void) | undefined"
        }
      },
      "onScrollCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onScrollCapture",
        "required": false,
        "type": {
          "name": "((event: UIEvent<SVGElement, UIEvent>) => void) | undefined"
        }
      },
      "onWheel": {
        "defaultValue": null,
        "description": "",
        "name": "onWheel",
        "required": false,
        "type": {
          "name": "((event: WheelEvent<SVGElement>) => void) | undefined"
        }
      },
      "onWheelCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onWheelCapture",
        "required": false,
        "type": {
          "name": "((event: WheelEvent<SVGElement>) => void) | undefined"
        }
      },
      "onAnimationStart": {
        "defaultValue": null,
        "description": "",
        "name": "onAnimationStart",
        "required": false,
        "type": {
          "name": "((event: AnimationEvent<SVGElement>) => void) | undefined"
        }
      },
      "onAnimationStartCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onAnimationStartCapture",
        "required": false,
        "type": {
          "name": "((event: AnimationEvent<SVGElement>) => void) | undefined"
        }
      },
      "onAnimationEnd": {
        "defaultValue": null,
        "description": "",
        "name": "onAnimationEnd",
        "required": false,
        "type": {
          "name": "((event: AnimationEvent<SVGElement>) => void) | undefined"
        }
      },
      "onAnimationEndCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onAnimationEndCapture",
        "required": false,
        "type": {
          "name": "((event: AnimationEvent<SVGElement>) => void) | undefined"
        }
      },
      "onAnimationIteration": {
        "defaultValue": null,
        "description": "",
        "name": "onAnimationIteration",
        "required": false,
        "type": {
          "name": "((event: AnimationEvent<SVGElement>) => void) | undefined"
        }
      },
      "onAnimationIterationCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onAnimationIterationCapture",
        "required": false,
        "type": {
          "name": "((event: AnimationEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTransitionEnd": {
        "defaultValue": null,
        "description": "",
        "name": "onTransitionEnd",
        "required": false,
        "type": {
          "name": "((event: TransitionEvent<SVGElement>) => void) | undefined"
        }
      },
      "onTransitionEndCapture": {
        "defaultValue": null,
        "description": "",
        "name": "onTransitionEndCapture",
        "required": false,
        "type": {
          "name": "((event: TransitionEvent<SVGElement>) => void) | undefined"
        }
      },
      "display": {
        "defaultValue": null,
        "description": "",
        "name": "display",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fontSize": {
        "defaultValue": null,
        "description": "",
        "name": "fontSize",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "filter": {
        "defaultValue": null,
        "description": "",
        "name": "filter",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "scale": {
        "defaultValue": null,
        "description": "",
        "name": "scale",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "width": {
        "defaultValue": null,
        "description": "",
        "name": "width",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "allowReorder": {
        "defaultValue": null,
        "description": "",
        "name": "allowReorder",
        "required": false,
        "type": {
          "name": "\"yes\" | \"no\" | undefined"
        }
      },
      "order": {
        "defaultValue": null,
        "description": "",
        "name": "order",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "direction": {
        "defaultValue": null,
        "description": "",
        "name": "direction",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "format": {
        "defaultValue": null,
        "description": "",
        "name": "format",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "href": {
        "defaultValue": null,
        "description": "",
        "name": "href",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "target": {
        "defaultValue": null,
        "description": "",
        "name": "target",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "clipPath": {
        "defaultValue": null,
        "description": "",
        "name": "clipPath",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "mask": {
        "defaultValue": null,
        "description": "",
        "name": "mask",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "path": {
        "defaultValue": null,
        "description": "",
        "name": "path",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "height": {
        "defaultValue": null,
        "description": "",
        "name": "height",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fontFamily": {
        "defaultValue": null,
        "description": "",
        "name": "fontFamily",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "max": {
        "defaultValue": null,
        "description": "",
        "name": "max",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "media": {
        "defaultValue": null,
        "description": "",
        "name": "media",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "method": {
        "defaultValue": null,
        "description": "",
        "name": "method",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "min": {
        "defaultValue": null,
        "description": "",
        "name": "min",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "name": {
        "defaultValue": null,
        "description": "",
        "name": "name",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "crossOrigin": {
        "defaultValue": null,
        "description": "",
        "name": "crossOrigin",
        "required": false,
        "type": {
          "name": "\"\" | \"anonymous\" | \"use-credentials\" | undefined"
        }
      },
      "accentHeight": {
        "defaultValue": null,
        "description": "",
        "name": "accentHeight",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "accumulate": {
        "defaultValue": null,
        "description": "",
        "name": "accumulate",
        "required": false,
        "type": {
          "name": "\"none\" | \"sum\" | undefined"
        }
      },
      "additive": {
        "defaultValue": null,
        "description": "",
        "name": "additive",
        "required": false,
        "type": {
          "name": "\"sum\" | \"replace\" | undefined"
        }
      },
      "alignmentBaseline": {
        "defaultValue": null,
        "description": "",
        "name": "alignmentBaseline",
        "required": false,
        "type": {
          "name": "\"inherit\" | \"alphabetic\" | \"hanging\" | \"ideographic\" | \"mathematical\" | \"auto\" | \"baseline\" | \"before-edge\" | \"text-before-edge\" | \"middle\" | \"central\" | \"after-edge\" | \"text-after-edge\" | undefined"
        }
      },
      "alphabetic": {
        "defaultValue": null,
        "description": "",
        "name": "alphabetic",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "amplitude": {
        "defaultValue": null,
        "description": "",
        "name": "amplitude",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "arabicForm": {
        "defaultValue": null,
        "description": "",
        "name": "arabicForm",
        "required": false,
        "type": {
          "name": "\"initial\" | \"medial\" | \"terminal\" | \"isolated\" | undefined"
        }
      },
      "ascent": {
        "defaultValue": null,
        "description": "",
        "name": "ascent",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "attributeName": {
        "defaultValue": null,
        "description": "",
        "name": "attributeName",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "attributeType": {
        "defaultValue": null,
        "description": "",
        "name": "attributeType",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "autoReverse": {
        "defaultValue": null,
        "description": "",
        "name": "autoReverse",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "azimuth": {
        "defaultValue": null,
        "description": "",
        "name": "azimuth",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "baseFrequency": {
        "defaultValue": null,
        "description": "",
        "name": "baseFrequency",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "baselineShift": {
        "defaultValue": null,
        "description": "",
        "name": "baselineShift",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "baseProfile": {
        "defaultValue": null,
        "description": "",
        "name": "baseProfile",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "bbox": {
        "defaultValue": null,
        "description": "",
        "name": "bbox",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "begin": {
        "defaultValue": null,
        "description": "",
        "name": "begin",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "bias": {
        "defaultValue": null,
        "description": "",
        "name": "bias",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "by": {
        "defaultValue": null,
        "description": "",
        "name": "by",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "calcMode": {
        "defaultValue": null,
        "description": "",
        "name": "calcMode",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "capHeight": {
        "defaultValue": null,
        "description": "",
        "name": "capHeight",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "clip": {
        "defaultValue": null,
        "description": "",
        "name": "clip",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "clipPathUnits": {
        "defaultValue": null,
        "description": "",
        "name": "clipPathUnits",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "clipRule": {
        "defaultValue": null,
        "description": "",
        "name": "clipRule",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "colorInterpolation": {
        "defaultValue": null,
        "description": "",
        "name": "colorInterpolation",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "colorInterpolationFilters": {
        "defaultValue": null,
        "description": "",
        "name": "colorInterpolationFilters",
        "required": false,
        "type": {
          "name": "\"inherit\" | \"auto\" | \"sRGB\" | \"linearRGB\" | undefined"
        }
      },
      "colorProfile": {
        "defaultValue": null,
        "description": "",
        "name": "colorProfile",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "colorRendering": {
        "defaultValue": null,
        "description": "",
        "name": "colorRendering",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "contentScriptType": {
        "defaultValue": null,
        "description": "",
        "name": "contentScriptType",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "contentStyleType": {
        "defaultValue": null,
        "description": "",
        "name": "contentStyleType",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "cursor": {
        "defaultValue": null,
        "description": "",
        "name": "cursor",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "cx": {
        "defaultValue": null,
        "description": "",
        "name": "cx",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "cy": {
        "defaultValue": null,
        "description": "",
        "name": "cy",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "d": {
        "defaultValue": null,
        "description": "",
        "name": "d",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "decelerate": {
        "defaultValue": null,
        "description": "",
        "name": "decelerate",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "descent": {
        "defaultValue": null,
        "description": "",
        "name": "descent",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "diffuseConstant": {
        "defaultValue": null,
        "description": "",
        "name": "diffuseConstant",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "divisor": {
        "defaultValue": null,
        "description": "",
        "name": "divisor",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "dominantBaseline": {
        "defaultValue": null,
        "description": "",
        "name": "dominantBaseline",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "dur": {
        "defaultValue": null,
        "description": "",
        "name": "dur",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "dx": {
        "defaultValue": null,
        "description": "",
        "name": "dx",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "dy": {
        "defaultValue": null,
        "description": "",
        "name": "dy",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "edgeMode": {
        "defaultValue": null,
        "description": "",
        "name": "edgeMode",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "elevation": {
        "defaultValue": null,
        "description": "",
        "name": "elevation",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "enableBackground": {
        "defaultValue": null,
        "description": "",
        "name": "enableBackground",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "end": {
        "defaultValue": null,
        "description": "",
        "name": "end",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "exponent": {
        "defaultValue": null,
        "description": "",
        "name": "exponent",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "externalResourcesRequired": {
        "defaultValue": null,
        "description": "",
        "name": "externalResourcesRequired",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "fill": {
        "defaultValue": null,
        "description": "",
        "name": "fill",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "fillOpacity": {
        "defaultValue": null,
        "description": "",
        "name": "fillOpacity",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fillRule": {
        "defaultValue": null,
        "description": "",
        "name": "fillRule",
        "required": false,
        "type": {
          "name": "\"inherit\" | \"nonzero\" | \"evenodd\" | undefined"
        }
      },
      "filterRes": {
        "defaultValue": null,
        "description": "",
        "name": "filterRes",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "filterUnits": {
        "defaultValue": null,
        "description": "",
        "name": "filterUnits",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "floodColor": {
        "defaultValue": null,
        "description": "",
        "name": "floodColor",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "floodOpacity": {
        "defaultValue": null,
        "description": "",
        "name": "floodOpacity",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "focusable": {
        "defaultValue": null,
        "description": "",
        "name": "focusable",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | \"auto\" | undefined"
        }
      },
      "fontSizeAdjust": {
        "defaultValue": null,
        "description": "",
        "name": "fontSizeAdjust",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fontStretch": {
        "defaultValue": null,
        "description": "",
        "name": "fontStretch",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fontStyle": {
        "defaultValue": null,
        "description": "",
        "name": "fontStyle",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fontVariant": {
        "defaultValue": null,
        "description": "",
        "name": "fontVariant",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fontWeight": {
        "defaultValue": null,
        "description": "",
        "name": "fontWeight",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "from": {
        "defaultValue": null,
        "description": "",
        "name": "from",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fx": {
        "defaultValue": null,
        "description": "",
        "name": "fx",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "fy": {
        "defaultValue": null,
        "description": "",
        "name": "fy",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "g1": {
        "defaultValue": null,
        "description": "",
        "name": "g1",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "g2": {
        "defaultValue": null,
        "description": "",
        "name": "g2",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "glyphName": {
        "defaultValue": null,
        "description": "",
        "name": "glyphName",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "glyphOrientationHorizontal": {
        "defaultValue": null,
        "description": "",
        "name": "glyphOrientationHorizontal",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "glyphOrientationVertical": {
        "defaultValue": null,
        "description": "",
        "name": "glyphOrientationVertical",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "glyphRef": {
        "defaultValue": null,
        "description": "",
        "name": "glyphRef",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "gradientTransform": {
        "defaultValue": null,
        "description": "",
        "name": "gradientTransform",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "gradientUnits": {
        "defaultValue": null,
        "description": "",
        "name": "gradientUnits",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "hanging": {
        "defaultValue": null,
        "description": "",
        "name": "hanging",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "horizAdvX": {
        "defaultValue": null,
        "description": "",
        "name": "horizAdvX",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "horizOriginX": {
        "defaultValue": null,
        "description": "",
        "name": "horizOriginX",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "ideographic": {
        "defaultValue": null,
        "description": "",
        "name": "ideographic",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "imageRendering": {
        "defaultValue": null,
        "description": "",
        "name": "imageRendering",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "in2": {
        "defaultValue": null,
        "description": "",
        "name": "in2",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "in": {
        "defaultValue": null,
        "description": "",
        "name": "in",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "intercept": {
        "defaultValue": null,
        "description": "",
        "name": "intercept",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "k1": {
        "defaultValue": null,
        "description": "",
        "name": "k1",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "k2": {
        "defaultValue": null,
        "description": "",
        "name": "k2",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "k3": {
        "defaultValue": null,
        "description": "",
        "name": "k3",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "k4": {
        "defaultValue": null,
        "description": "",
        "name": "k4",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "k": {
        "defaultValue": null,
        "description": "",
        "name": "k",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "kernelMatrix": {
        "defaultValue": null,
        "description": "",
        "name": "kernelMatrix",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "kernelUnitLength": {
        "defaultValue": null,
        "description": "",
        "name": "kernelUnitLength",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "kerning": {
        "defaultValue": null,
        "description": "",
        "name": "kerning",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "keyPoints": {
        "defaultValue": null,
        "description": "",
        "name": "keyPoints",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "keySplines": {
        "defaultValue": null,
        "description": "",
        "name": "keySplines",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "keyTimes": {
        "defaultValue": null,
        "description": "",
        "name": "keyTimes",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "lengthAdjust": {
        "defaultValue": null,
        "description": "",
        "name": "lengthAdjust",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "letterSpacing": {
        "defaultValue": null,
        "description": "",
        "name": "letterSpacing",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "lightingColor": {
        "defaultValue": null,
        "description": "",
        "name": "lightingColor",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "limitingConeAngle": {
        "defaultValue": null,
        "description": "",
        "name": "limitingConeAngle",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "local": {
        "defaultValue": null,
        "description": "",
        "name": "local",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "markerEnd": {
        "defaultValue": null,
        "description": "",
        "name": "markerEnd",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "markerHeight": {
        "defaultValue": null,
        "description": "",
        "name": "markerHeight",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "markerMid": {
        "defaultValue": null,
        "description": "",
        "name": "markerMid",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "markerStart": {
        "defaultValue": null,
        "description": "",
        "name": "markerStart",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "markerUnits": {
        "defaultValue": null,
        "description": "",
        "name": "markerUnits",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "markerWidth": {
        "defaultValue": null,
        "description": "",
        "name": "markerWidth",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "maskContentUnits": {
        "defaultValue": null,
        "description": "",
        "name": "maskContentUnits",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "maskUnits": {
        "defaultValue": null,
        "description": "",
        "name": "maskUnits",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "mathematical": {
        "defaultValue": null,
        "description": "",
        "name": "mathematical",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "mode": {
        "defaultValue": null,
        "description": "",
        "name": "mode",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "numOctaves": {
        "defaultValue": null,
        "description": "",
        "name": "numOctaves",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "offset": {
        "defaultValue": null,
        "description": "",
        "name": "offset",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "opacity": {
        "defaultValue": null,
        "description": "",
        "name": "opacity",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "operator": {
        "defaultValue": null,
        "description": "",
        "name": "operator",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "orient": {
        "defaultValue": null,
        "description": "",
        "name": "orient",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "orientation": {
        "defaultValue": null,
        "description": "",
        "name": "orientation",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "origin": {
        "defaultValue": null,
        "description": "",
        "name": "origin",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "overflow": {
        "defaultValue": null,
        "description": "",
        "name": "overflow",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "overlinePosition": {
        "defaultValue": null,
        "description": "",
        "name": "overlinePosition",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "overlineThickness": {
        "defaultValue": null,
        "description": "",
        "name": "overlineThickness",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "paintOrder": {
        "defaultValue": null,
        "description": "",
        "name": "paintOrder",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "panose1": {
        "defaultValue": null,
        "description": "",
        "name": "panose1",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "pathLength": {
        "defaultValue": null,
        "description": "",
        "name": "pathLength",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "patternContentUnits": {
        "defaultValue": null,
        "description": "",
        "name": "patternContentUnits",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "patternTransform": {
        "defaultValue": null,
        "description": "",
        "name": "patternTransform",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "patternUnits": {
        "defaultValue": null,
        "description": "",
        "name": "patternUnits",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "pointerEvents": {
        "defaultValue": null,
        "description": "",
        "name": "pointerEvents",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "points": {
        "defaultValue": null,
        "description": "",
        "name": "points",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "pointsAtX": {
        "defaultValue": null,
        "description": "",
        "name": "pointsAtX",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "pointsAtY": {
        "defaultValue": null,
        "description": "",
        "name": "pointsAtY",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "pointsAtZ": {
        "defaultValue": null,
        "description": "",
        "name": "pointsAtZ",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "preserveAlpha": {
        "defaultValue": null,
        "description": "",
        "name": "preserveAlpha",
        "required": false,
        "type": {
          "name": "boolean | \"true\" | \"false\" | undefined"
        }
      },
      "preserveAspectRatio": {
        "defaultValue": null,
        "description": "",
        "name": "preserveAspectRatio",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "primitiveUnits": {
        "defaultValue": null,
        "description": "",
        "name": "primitiveUnits",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "r": {
        "defaultValue": null,
        "description": "",
        "name": "r",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "radius": {
        "defaultValue": null,
        "description": "",
        "name": "radius",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "refX": {
        "defaultValue": null,
        "description": "",
        "name": "refX",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "refY": {
        "defaultValue": null,
        "description": "",
        "name": "refY",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "renderingIntent": {
        "defaultValue": null,
        "description": "",
        "name": "renderingIntent",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "repeatCount": {
        "defaultValue": null,
        "description": "",
        "name": "repeatCount",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "repeatDur": {
        "defaultValue": null,
        "description": "",
        "name": "repeatDur",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "requiredExtensions": {
        "defaultValue": null,
        "description": "",
        "name": "requiredExtensions",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "requiredFeatures": {
        "defaultValue": null,
        "description": "",
        "name": "requiredFeatures",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "restart": {
        "defaultValue": null,
        "description": "",
        "name": "restart",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "result": {
        "defaultValue": null,
        "description": "",
        "name": "result",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "rotate": {
        "defaultValue": null,
        "description": "",
        "name": "rotate",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "rx": {
        "defaultValue": null,
        "description": "",
        "name": "rx",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "ry": {
        "defaultValue": null,
        "description": "",
        "name": "ry",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "seed": {
        "defaultValue": null,
        "description": "",
        "name": "seed",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "shapeRendering": {
        "defaultValue": null,
        "description": "",
        "name": "shapeRendering",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "slope": {
        "defaultValue": null,
        "description": "",
        "name": "slope",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "spacing": {
        "defaultValue": null,
        "description": "",
        "name": "spacing",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "specularConstant": {
        "defaultValue": null,
        "description": "",
        "name": "specularConstant",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "specularExponent": {
        "defaultValue": null,
        "description": "",
        "name": "specularExponent",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "speed": {
        "defaultValue": null,
        "description": "",
        "name": "speed",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "spreadMethod": {
        "defaultValue": null,
        "description": "",
        "name": "spreadMethod",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "startOffset": {
        "defaultValue": null,
        "description": "",
        "name": "startOffset",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "stdDeviation": {
        "defaultValue": null,
        "description": "",
        "name": "stdDeviation",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "stemh": {
        "defaultValue": null,
        "description": "",
        "name": "stemh",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "stemv": {
        "defaultValue": null,
        "description": "",
        "name": "stemv",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "stitchTiles": {
        "defaultValue": null,
        "description": "",
        "name": "stitchTiles",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "stopColor": {
        "defaultValue": null,
        "description": "",
        "name": "stopColor",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "stopOpacity": {
        "defaultValue": null,
        "description": "",
        "name": "stopOpacity",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "strikethroughPosition": {
        "defaultValue": null,
        "description": "",
        "name": "strikethroughPosition",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "strikethroughThickness": {
        "defaultValue": null,
        "description": "",
        "name": "strikethroughThickness",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "stroke": {
        "defaultValue": null,
        "description": "",
        "name": "stroke",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "strokeDasharray": {
        "defaultValue": null,
        "description": "",
        "name": "strokeDasharray",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "strokeDashoffset": {
        "defaultValue": null,
        "description": "",
        "name": "strokeDashoffset",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "strokeLinecap": {
        "defaultValue": null,
        "description": "",
        "name": "strokeLinecap",
        "required": false,
        "type": {
          "name": "\"inherit\" | \"round\" | \"butt\" | \"square\" | undefined"
        }
      },
      "strokeLinejoin": {
        "defaultValue": null,
        "description": "",
        "name": "strokeLinejoin",
        "required": false,
        "type": {
          "name": "\"inherit\" | \"round\" | \"miter\" | \"bevel\" | undefined"
        }
      },
      "strokeMiterlimit": {
        "defaultValue": null,
        "description": "",
        "name": "strokeMiterlimit",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "strokeOpacity": {
        "defaultValue": null,
        "description": "",
        "name": "strokeOpacity",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "strokeWidth": {
        "defaultValue": null,
        "description": "",
        "name": "strokeWidth",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "surfaceScale": {
        "defaultValue": null,
        "description": "",
        "name": "surfaceScale",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "systemLanguage": {
        "defaultValue": null,
        "description": "",
        "name": "systemLanguage",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "tableValues": {
        "defaultValue": null,
        "description": "",
        "name": "tableValues",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "targetX": {
        "defaultValue": null,
        "description": "",
        "name": "targetX",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "targetY": {
        "defaultValue": null,
        "description": "",
        "name": "targetY",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "textAnchor": {
        "defaultValue": null,
        "description": "",
        "name": "textAnchor",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "textDecoration": {
        "defaultValue": null,
        "description": "",
        "name": "textDecoration",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "textLength": {
        "defaultValue": null,
        "description": "",
        "name": "textLength",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "textRendering": {
        "defaultValue": null,
        "description": "",
        "name": "textRendering",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "to": {
        "defaultValue": null,
        "description": "",
        "name": "to",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "transform": {
        "defaultValue": null,
        "description": "",
        "name": "transform",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "u1": {
        "defaultValue": null,
        "description": "",
        "name": "u1",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "u2": {
        "defaultValue": null,
        "description": "",
        "name": "u2",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "underlinePosition": {
        "defaultValue": null,
        "description": "",
        "name": "underlinePosition",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "underlineThickness": {
        "defaultValue": null,
        "description": "",
        "name": "underlineThickness",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "unicode": {
        "defaultValue": null,
        "description": "",
        "name": "unicode",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "unicodeBidi": {
        "defaultValue": null,
        "description": "",
        "name": "unicodeBidi",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "unicodeRange": {
        "defaultValue": null,
        "description": "",
        "name": "unicodeRange",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "unitsPerEm": {
        "defaultValue": null,
        "description": "",
        "name": "unitsPerEm",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "vAlphabetic": {
        "defaultValue": null,
        "description": "",
        "name": "vAlphabetic",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "values": {
        "defaultValue": null,
        "description": "",
        "name": "values",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "vectorEffect": {
        "defaultValue": null,
        "description": "",
        "name": "vectorEffect",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "version": {
        "defaultValue": null,
        "description": "",
        "name": "version",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "vertAdvY": {
        "defaultValue": null,
        "description": "",
        "name": "vertAdvY",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "vertOriginX": {
        "defaultValue": null,
        "description": "",
        "name": "vertOriginX",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "vertOriginY": {
        "defaultValue": null,
        "description": "",
        "name": "vertOriginY",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "vHanging": {
        "defaultValue": null,
        "description": "",
        "name": "vHanging",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "vIdeographic": {
        "defaultValue": null,
        "description": "",
        "name": "vIdeographic",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "viewBox": {
        "defaultValue": null,
        "description": "",
        "name": "viewBox",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "viewTarget": {
        "defaultValue": null,
        "description": "",
        "name": "viewTarget",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "visibility": {
        "defaultValue": null,
        "description": "",
        "name": "visibility",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "vMathematical": {
        "defaultValue": null,
        "description": "",
        "name": "vMathematical",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "widths": {
        "defaultValue": null,
        "description": "",
        "name": "widths",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "wordSpacing": {
        "defaultValue": null,
        "description": "",
        "name": "wordSpacing",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "writingMode": {
        "defaultValue": null,
        "description": "",
        "name": "writingMode",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "x1": {
        "defaultValue": null,
        "description": "",
        "name": "x1",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "x2": {
        "defaultValue": null,
        "description": "",
        "name": "x2",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "x": {
        "defaultValue": null,
        "description": "",
        "name": "x",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "xChannelSelector": {
        "defaultValue": null,
        "description": "",
        "name": "xChannelSelector",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xHeight": {
        "defaultValue": null,
        "description": "",
        "name": "xHeight",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "xlinkActuate": {
        "defaultValue": null,
        "description": "",
        "name": "xlinkActuate",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xlinkArcrole": {
        "defaultValue": null,
        "description": "",
        "name": "xlinkArcrole",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xlinkHref": {
        "defaultValue": null,
        "description": "",
        "name": "xlinkHref",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xlinkRole": {
        "defaultValue": null,
        "description": "",
        "name": "xlinkRole",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xlinkShow": {
        "defaultValue": null,
        "description": "",
        "name": "xlinkShow",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xlinkTitle": {
        "defaultValue": null,
        "description": "",
        "name": "xlinkTitle",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xlinkType": {
        "defaultValue": null,
        "description": "",
        "name": "xlinkType",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xmlBase": {
        "defaultValue": null,
        "description": "",
        "name": "xmlBase",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xmlLang": {
        "defaultValue": null,
        "description": "",
        "name": "xmlLang",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xmlns": {
        "defaultValue": null,
        "description": "",
        "name": "xmlns",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xmlnsXlink": {
        "defaultValue": null,
        "description": "",
        "name": "xmlnsXlink",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "xmlSpace": {
        "defaultValue": null,
        "description": "",
        "name": "xmlSpace",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "y1": {
        "defaultValue": null,
        "description": "",
        "name": "y1",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "y2": {
        "defaultValue": null,
        "description": "",
        "name": "y2",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "y": {
        "defaultValue": null,
        "description": "",
        "name": "y",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "yChannelSelector": {
        "defaultValue": null,
        "description": "",
        "name": "yChannelSelector",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "z": {
        "defaultValue": null,
        "description": "",
        "name": "z",
        "required": false,
        "type": {
          "name": "string | number | undefined"
        }
      },
      "zoomAndPan": {
        "defaultValue": null,
        "description": "",
        "name": "zoomAndPan",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "type": {
        "defaultValue": null,
        "description": "`Enum` is any of the named icons listed in the docs, `string` is usually a URL to an SVG file, and `elementType` is any React SVG component",
        "name": "type",
        "required": true,
        "type": {
          "name": "IconType"
        }
      },
      "color": {
        "defaultValue": null,
        "description": "One of EUI's color palette or a valid CSS color value https://developer.mozilla.org/en-US/docs/Web/CSS/color_value.\nNote that coloring only works if your SVG is removed of fill attributes.",
        "name": "color",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "size": {
        "defaultValue": null,
        "description": "Note that every size other than `original` assumes the provided SVG sits on a square viewbox.",
        "name": "size",
        "required": false,
        "type": {
          "name": "\"s\" | \"m\" | \"l\" | \"xl\" | \"original\" | \"xxl\" | undefined"
        }
      },
      "title": {
        "defaultValue": null,
        "description": "Descriptive title for naming the icon based on its use",
        "name": "title",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "titleId": {
        "defaultValue": null,
        "description": "A unique identifier for the title element",
        "name": "titleId",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "onIconLoad": {
        "defaultValue": null,
        "description": "Callback when the icon has been loaded & rendered",
        "name": "onIconLoad",
        "required": false,
        "type": {
          "name": "(() => void) | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/icon/icon.tsx#EuiIcon"] = {
      docgenInfo: EuiIcon.__docgenInfo,
      name: "EuiIcon",
      path: "src/components/icon/icon.tsx#EuiIcon"
    };
} catch (__react_docgen_typescript_loader_error) {}